import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import styled from '@emotion/styled';

import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import useWindowSize from '../../hooks/useWindowSize';

import activeDot from '@images/home/spread-modal-nav-dot-active.svg';
import closeIcon from '@images/home/spread/close_modal.svg';
import inactiveDot from '@images/home/spread-modal-nav-dot-inactive.svg';
import nextButton from '@images/home/spread/next_button.svg';
import prevButton from '@images/home/spread/previous_button.svg';
import PreloadImages from '../PreloadImages';

type PropsType = {};

const maxMobileScreenSize = 767; // px
const modalDesktopSideMargin = 95; // px
const modalHeightWidthRatio = 0.48;
const pageSection = document.querySelector('.home-spread');

const Overlay = styled.div`
  cursor: default;
  position: fixed;
  background-color: rgba(255, 255, 255, 0.5);
  z-index: 100;

  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const ModalBackground = styled.div`
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.25);
  position: fixed;
`;

const ImageContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  padding: 8rem 12rem;
`;

const Image = styled.img`
  max-height: 100%;
  max-width: 100%;
`;

const CloseIcon = styled.button`
  background: transparent;
  position: absolute;
  font-size: 0;
  padding: 1rem;
  right: 1rem;
  top: 1rem;
`;

const NavButton = styled.button`
  font-size: 0;
  position: absolute;
`;

function ModalImage({
  data: { src, alt }
}: {
  data: { src: string, alt: string }
}) {
  return (
    <ImageContainer>
      <Image src={src} alt={alt} />{' '}
    </ImageContainer>
  );
}

function PrevButton({ height, onClick, top, width }) {
  return (
    <NavButton style={{ height, top, width, left: 0 }} onClick={onClick}>
      <img src={prevButton} alt="" />
    </NavButton>
  );
}

function NextButton({ height, onClick, top, width }) {
  return (
    <NavButton style={{ height, top, width, right: 0 }} onClick={onClick}>
      <img src={nextButton} alt="" />
    </NavButton>
  );
}

function ModalArrowControls({
  modalHeight,
  modalWidth,
  onClickBack,
  onClickForward
}) {
  const [height, setHeight] = useState(0);
  const [top, setTop] = useState(0);
  const [width, setWidth] = useState(0);
  const naturalHeight = 145; // px
  const naturalWidth = 75; // px
  const heightWidthRatio = naturalHeight / naturalWidth;

  useLayoutEffect(() => {
    const maxButtonHeight = Math.min(
      (modalWidth / 15) * heightWidthRatio,
      naturalHeight
    );

    const buttonHeight = Math.min(modalHeight / 2, maxButtonHeight);
    const buttonWidth = buttonHeight / heightWidthRatio;

    setHeight(buttonHeight);
    setTop(modalHeight / 2 - buttonHeight / 2);
    setWidth(buttonWidth);
  }, [heightWidthRatio, modalHeight, modalWidth]);

  return [
    <PrevButton
      height={height}
      top={top}
      width={width}
      key="prev"
      onClick={onClickBack}
    />,
    <NextButton
      height={height}
      top={top}
      width={width}
      key="next"
      onClick={onClickForward}
    />
  ];
}

function ModalDotControls({
  activeIndex,
  count,
  onClick
}: {
  activeIndex: number;
  count: number;
  onClick: (index: number) => void;
}) {
  return (
    <div className="home-spread-modal-nav-dots">
      {new Array(count).fill(null).map((_, index) => {
        return (
          <button key={index} onClick={() => onClick(index)}>
            <img src={index === activeIndex ? activeDot : inactiveDot} />
          </button>
        );
      })}
    </div>
  );
}

function ModalCloseControl({ onClick }) {
  return (
    <CloseIcon onClick={onClick}>
      <img src={closeIcon} />
    </CloseIcon>
  );
}

function Modal({ clickedImageIndex, images, onClose }) {
  const windowSize = useWindowSize();
  const [activeIndex, setActiveIndex] = useState(clickedImageIndex);
  const [width, setWidth] = useState(0);
  const [pageSectionWidth, setPageSectionWidth] = useState(0);
  const modalRef = useRef<HTMLDivElement | null>(null);

  useLockBodyScroll();

  const handleBackClick = useCallback(() => {
    setActiveIndex(activeIndex === 0 ? images.length - 1 : activeIndex - 1);
  }, [activeIndex, images.length]);

  const handleForwardClick = useCallback(() => {
    setActiveIndex(activeIndex === images.length - 1 ? 0 : activeIndex + 1);
  }, [activeIndex, images.length]);

  useLayoutEffect(() => {
    if (!pageSection) return;

    const pageSectionLocation = pageSection.getBoundingClientRect();

    setPageSectionWidth(pageSectionLocation.width);
  }, [windowSize.width]); // fire on window resize

  useLayoutEffect(() => {
    setActiveIndex(clickedImageIndex);
  }, [clickedImageIndex]);

  useEffect(() => {
    if (!modalRef.current) return;

    setWidth(modalRef.current.clientWidth);
  }, [windowSize.width, pageSectionWidth]);

  if (!pageSection) return null;

  let sideMargin = modalDesktopSideMargin;

  if (pageSectionWidth && windowSize.height && windowSize.width) {
    const height = pageSectionWidth * modalHeightWidthRatio;
    const pageSectionLeftPositionInBrowser =
      (windowSize.width - pageSectionWidth) / 2;

    return (
      <ModalBackground
        ref={modalRef}
        style={{
          height,
          width: pageSectionWidth - 2 * sideMargin,
          left: pageSectionLeftPositionInBrowser + sideMargin,
          top: windowSize.height / 2 - height / 2
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalImage data={images[activeIndex]} />
        <ModalArrowControls
          modalHeight={height}
          modalWidth={width}
          onClickBack={handleBackClick}
          onClickForward={handleForwardClick}
        />
        <ModalDotControls
          activeIndex={activeIndex}
          count={images.length}
          onClick={setActiveIndex}
        />
        <ModalCloseControl onClick={onClose} />
      </ModalBackground>
    );
  } else {
    return null;
  }
}

function SpreadCarousel(props: PropsType) {
  const windowSize = useWindowSize();
  const [showModal, setShowModal] = useState(false);
  const [clickedImageIndex, setClickedImageIndex] = useState(0);
  const [images, setImages] = useState([]);

  const showModalNow = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      if (!windowSize.width || windowSize.width < maxMobileScreenSize) return;

      setClickedImageIndex(
        images.findIndex((img) => img.el === e.currentTarget)
      );
      setShowModal(true);
    },
    [images, windowSize.width]
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  useEffect(() => {
    if (!pageSection) return;

    const images = [].slice.apply(pageSection.querySelectorAll('img'));

    if (!images.length) return;

    images.forEach((img) => img.addEventListener('click', showModalNow));

    return () =>
      images.forEach((img) => img.removeEventListener('click', showModalNow));
  }, [showModalNow]);

  useEffect(() => {
    if (!pageSection) return;

    const displayedImages = pageSection.querySelectorAll('img');

    // This uses parentElement which is terrible. We shouldn't rely on the HTML
    // structure.
    setImages(
      [].slice
        .apply(displayedImages)
        .map((img) => {
          return {
            alt: img.getAttribute('alt'),
            el: img,
            src: img.dataset.largeSrc,
            order: parseInt(window.getComputedStyle(img.parentElement).order)
          };
        })
        .sort((a, b) => a.order - b.order)
    );
  }, []);

  if (showModal && images) {
    return (
      <Overlay onClick={closeModal}>
        <PreloadImages images={images.map((it) => it.src)} />
        <Modal
          images={images}
          clickedImageIndex={clickedImageIndex}
          onClose={closeModal}
        />
      </Overlay>
    );
  } else {
    return null;
  }
}

export default (props: PropsType) => <SpreadCarousel {...props} />;
